// Emergency mode's staff screens (PMU-273, E3): declare (drill or real), the band on every screen
// while one is open, and the Emergency screen: my class's roll call, the school board, reunification,
// the end. Everything is recorded on this phone and goes over the Bluetooth mesh when the internet
// and the Wi-Fi are down (shared/emergency.ts), and to the cloud when it is up. The Emergency screen
// is the whole screen and never scrolls as a page: only the list inside it does.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Button, Dialog, Icon, Spinner, TextField, useToast, type IconName } from "@vavr/ui";
import { api, emergencyController, navigate, onEmergencyMessage, useAsync, useEmergency, usePath, type Incident, type MeshMessage } from "@vavr/screens/shared";
import { T, useTeacher, type Board, type BoardChild } from "@vavr/screens/teacher";
import { readRoster, type Roster } from "@vavr/client";
import "./emergency.css";

const KINDS: { id: string; label: string; hint: string; icon: IconName }[] = [
  { id: "lockdown", label: "Lockdown", hint: "Threat in or near the school", icon: "lock" },
  { id: "evacuation", label: "Evacuation", hint: "Leave for the assembly point", icon: "leave" },
  { id: "shelter", label: "Shelter in place", hint: "Stay inside, away from windows", icon: "home" },
  { id: "reunification", label: "Reunification", hint: "Hand children to approved adults", icon: "child" },
];
const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS.map((k) => [k.id, k.label]));
const KIND_ICON: Record<string, IconName> = Object.fromEntries(KINDS.map((k) => [k.id, k.icon]));

export const STATUS = [
  { id: "present", label: "Present" },
  { id: "missing", label: "Missing" },
  { id: "injured", label: "Injured" },
  { id: "with_adult", label: "With another adult" },
  { id: "absent_today", label: "Absent today" },
] as const;
const STATUS_LABEL: Record<string, string> = { ...Object.fromEntries(STATUS.map((s) => [s.id, s.label])), released: "Gone home" };

const clock = (at: number) => new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const newIncidentId = () => "inc_" + crypto.randomUUID().replace(/-/g, "").slice(0, 16);
const isOpen = (i: Incident | null): i is Incident => !!i && i.endedAt === null;
const title = (i: Incident) => `${i.drill ? "Drill · " : ""}${KIND_LABEL[i.kind] ?? "Emergency"}`;
const leave = () => navigate(T);

export const ALERTS = [
  { id: "help", label: "Need help", title: "Help needed", icon: "warning" as IconName },
  { id: "medical", label: "Medical", title: "Medical emergency", icon: "plus" as IconName },
  { id: "all_clear", label: "All clear here", title: "All clear", icon: "check-circle" as IconName },
] as const;
const ALERT_TITLE: Record<string, string> = Object.fromEntries(ALERTS.map((a) => [a.id, a.title]));
/** Whether this phone's user sees a message: everyone's, theirs, and the ones they sent. */
const forMe = (m: MeshMessage, me: string) => m.kind === "alert" || !m.to || m.to === me || m.by === me;

/** The band above every staff screen while an incident is open: red for real, striped for a drill. */
export function EmergencyBanner({ children }: { children: ReactNode }) {
  const { incident } = useEmergency();
  const path = usePath();
  const { me, device } = useTeacher();
  const toast = useToast();
  // A message or an alert from someone else, while the app is on screen: a toast (the service
  // notifies when the app is away). Old ones replayed at start do not ring.
  useEffect(() => onEmergencyMessage((m) => {
    if (m.device === device?.device_id || m.at < Date.now() - 120_000 || !forMe(m, me.id)) return;
    if (m.kind === "alert") navigator.vibrate?.(m.alert === "all_clear" ? 200 : [600, 250, 600, 250, 600]); else navigator.vibrate?.(200);
    toast.show({
      title: m.kind === "alert" ? `${ALERT_TITLE[m.alert ?? ""] ?? "Alert"}${m.place ? ` · ${m.place}` : ""}` : m.to ? "Message to you" : "Emergency message",
      description: m.kind === "alert" ? undefined : m.text, tone: m.kind === "alert" && m.alert !== "all_clear" ? "danger" : "info", priority: "high", timeout: m.kind === "alert" ? 0 : 10_000,
    });
  }), [device?.device_id, me.id, toast]);
  const here = path.startsWith(`${T}/emergency`);
  return (
    <>
      {isOpen(incident) && !here && (
        <button type="button" className="pme-band" data-drill={incident.drill} onClick={() => navigate(`${T}/emergency`)}
          aria-label={`${title(incident)}${incident.place ? ` at ${incident.place}` : ""}. Open the emergency screen.`}>
          <span className="pme-band__kind">{title(incident)}</span>
          <span className="pme-band__where">{incident.place ? `${incident.place} · ` : ""}since {clock(incident.declaredAt)}</span>
          <span className="pme-band__open">Open</span>
        </button>
      )}
      {children}
    </>
  );
}

/** On Now: the way in. Clearly an emergency control, out of the way of a stray tap (declaring takes a hold). */
export function EmergencyBar() {
  const { incident } = useEmergency();
  if (isOpen(incident)) return null;
  return (
    <button type="button" className="pme-cta" onClick={() => navigate(`${T}/emergency`)}>
      <span className="pme-cta__icon" aria-hidden="true"><Icon name="warning" /></span>
      <span className="pme-cta__text"><b>Emergency</b><small>Lockdown, evacuation, shelter, reunification</small></span>
      <Icon name="chevron-right" />
    </button>
  );
}

/** The Emergency screen: declare one, or follow and run the open one. */
export function EmergencyScreen() {
  const { incident } = useEmergency();
  return isOpen(incident) ? <OpenIncident incident={incident} /> : <Declare />;
}

/** A button that acts only after being held (a pocket or a stray tap cannot declare an emergency). */
function HoldButton({ ms, tone, disabled, onDone, children }: { ms: number; tone: "real" | "drill"; disabled?: boolean; onDone: () => void; children: ReactNode }) {
  const [p, setP] = useState(0);
  const raf = useRef(0);
  const begin = () => {
    if (disabled || raf.current) return;
    const t0 = performance.now();
    navigator.vibrate?.(15);
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      setP(k);
      if (k < 1) { raf.current = requestAnimationFrame(step); return; }
      raf.current = 0; setP(0); navigator.vibrate?.(120); onDone();
    };
    raf.current = requestAnimationFrame(step);
  };
  const cancel = () => { if (raf.current) cancelAnimationFrame(raf.current); raf.current = 0; setP(0); };
  useEffect(() => cancel, []);
  return (
    <button type="button" className="pme-hold" data-tone={tone} disabled={disabled} style={{ "--p": p } as CSSProperties}
      onPointerDown={begin} onPointerUp={cancel} onPointerLeave={cancel} onPointerCancel={cancel} onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); begin(); } }} onKeyUp={cancel}>
      <span className="pme-hold__fill" aria-hidden="true" />
      <span className="pme-hold__label">{children}</span>
    </button>
  );
}

function Declare() {
  const toast = useToast();
  const [kind, setKind] = useState<string | null>(null);
  const [drill, setDrill] = useState<boolean | null>(null);
  const [place, setPlace] = useState("");
  const [busy, setBusy] = useState(false);
  const needPlace = kind === "reunification";
  const ready = kind !== null && drill !== null && (!needPlace || place.trim() !== "");
  const declare = async () => {
    const ctl = emergencyController();
    if (!ctl || !kind || drill === null) { toast.show({ title: "Set up this phone first (Me).", tone: "danger" }); return; }
    setBusy(true);
    try {
      await ctl.record("emergency:declare", null, { incident: newIncidentId(), kind, drill, place: place.trim() || null, note: null });
      toast.show({ title: drill ? "Drill declared" : "Emergency declared", tone: drill ? "info" : "danger" });
    } catch (e) { toast.show({ title: (e as Error).message, tone: "danger" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="pme-screen">
      <header className="pme-top">
        <button type="button" className="pme-top__close" onClick={leave} aria-label="Close"><Icon name="close" /></button>
        <h1 className="pme-top__title">Emergency</h1>
      </header>
      <p className="pme-q" id="pme-what">What is happening?</p>
      <div className="pme-kinds" role="group" aria-labelledby="pme-what">
        {KINDS.map((k) => (
          <button key={k.id} type="button" className="pme-kind" aria-pressed={kind === k.id} onClick={() => setKind(k.id)}>
            <Icon name={k.icon} />
            <b>{k.label}</b>
            <small>{k.hint}</small>
          </button>
        ))}
      </div>
      <p className="pme-q" id="pme-drill">Drill or real?</p>
      <div className="pme-seg" role="group" aria-labelledby="pme-drill">
        <button type="button" data-tone="drill" aria-pressed={drill === true} onClick={() => setDrill(true)}>Drill</button>
        <button type="button" data-tone="real" aria-pressed={drill === false} onClick={() => setDrill(false)}>Real emergency</button>
      </div>
      <TextField label={needPlace ? "Meeting point" : "Where (optional)"} value={place} maxLength={120} onChange={(e) => setPlace(e.currentTarget.value)}
        placeholder={needPlace ? "e.g. the church car park" : "e.g. the gym, the north field"} />
      <div className="pme-foot">
        <HoldButton ms={drill === false ? 2000 : 1000} tone={drill === false ? "real" : "drill"} disabled={!ready || busy} onDone={() => void declare()}>
          {busy ? <Spinner label="Declaring" /> : !ready ? (kind === null ? "Choose what is happening" : drill === null ? "Choose drill or real" : "Add the meeting point") : drill ? "Hold to start the drill" : "Hold to declare the emergency"}
        </HoldButton>
        <p className="pme-foot__hint">Every staff phone and door at the school is told at once, by internet, Wi-Fi or Bluetooth.</p>
      </div>
    </div>
  );
}

function OpenIncident({ incident }: { incident: Incident }) {
  const { site, db, device, me } = useTeacher();
  const toast = useToast();
  const board = useAsync(() => api<Board>("GET", `/api/v1/sites/${site.id}/board`), [site.id]);
  const roster = useAsync<Roster | null>(async () => (device ? readRoster(db, device.mirrorKey).catch(() => null) : null), [db, device?.device_id]);
  const [mesh, setMesh] = useState<{ mode: string; links: number } | null>(null);
  const [tab, setTab] = useState<"class" | "school" | "messages">("class");
  const [seen, setSeen] = useState(() => Date.now());
  const [text, setText] = useState("");
  const [to, setTo] = useState("");
  const [alerting, setAlerting] = useState<string | null>(null);
  const [alertPlace, setAlertPlace] = useState(incident.place ?? "");
  const [sel, setSel] = useState<BoardChild | null>(null);
  const [ending, setEnding] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const poll = () => { void emergencyController()?.meshStatus().then(setMesh); };
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);

  const kids = board.data?.children ?? [];
  const roomName = (id: string | null) => roster.data?.rooms.find((r) => r.id === id)?.name ?? "";
  const rooms = useMemo(() => [...new Set(kids.map((c) => c.room_id))], [kids]);
  const marks = incident.rollCall;
  const statusOf = (c: BoardChild) => (incident.released[c.id] ? "released" : marks[c.id]?.status ?? null);
  const counts = useMemo(() => {
    const n = { present: 0, missing: 0, injured: 0, with_adult: 0, absent_today: 0, released: 0, unmarked: 0 };
    for (const c of kids) n[(statusOf(c) ?? "unmarked") as keyof typeof n]++;
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kids, incident]);

  const rec = (childId: string | null, type: "emergency:roll_call" | "emergency:class_report" | "emergency:end", data: Record<string, string | number | boolean | null>) =>
    emergencyController()?.record(type, childId, { incident: incident.id, ...data });
  const run = async (f: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await f(); if (ok) toast.show({ title: ok, tone: "success" }); }
    catch (e) { toast.show({ title: (e as Error).message, tone: "danger" }); }
    finally { setBusy(false); }
  };
  const mark = (c: BoardChild, status: string) => { setSel(null); void run(() => rec(c.id, "emergency:roll_call", { status, place: incident.place }) ?? Promise.resolve()); };
  // Only children in school today are present; those who never came (or went home before) are absent today.
  const markRest = () => run(async () => { for (const c of kids) if (!statusOf(c)) await rec(c.id, "emergency:roll_call", { status: c.state === "in" ? "present" : "absent_today", place: incident.place }); });
  const report = () => run(async () => {
    for (const room of rooms) {
      const inRoom = kids.filter((c) => c.room_id === room);
      const missing = inRoom.filter((c) => { const s = statusOf(c); return s === null || s === "missing"; }).map((c) => c.id);
      await rec(null, "emergency:class_report", { room_id: room ?? "", accounted: inRoom.length - missing.length, missing: missing.join(","), complete: missing.length === 0 });
    }
  }, "Class reported");

  const missingIds = Object.entries(marks).filter(([id, m]) => m.status === "missing" && !incident.released[id]).map(([id]) => id);
  // A child from another class is not on this phone (a teacher's phone holds its own classes only):
  // say whose class and who marked them, so staff know whom to ask.
  const nameOf = (id: string) => {
    const c = kids.find((k) => k.id === id) ?? roster.data?.children.find((k) => k.id === id);
    if (c) return `${c.first} ${c.last}`;
    const roomId = Object.entries(incident.reports).find(([, r]) => r.missing.includes(id))?.[0];
    const room = roster.data?.rooms.find((r) => r.id === roomId)?.name;
    const by = roster.data?.staff.find((st) => st.id === marks[id]?.by)?.name;
    return `A child${room ? ` in ${room}` : ""}${by ? `, marked by ${by}` : ""}`;
  };
  const allRooms = roster.data?.rooms ?? [];
  const staffName = (id: string) => (id === me.id ? "You" : roster.data?.staff.find((st) => st.id === id)?.name ?? "A member of staff");
  const msgs = incident.messages.filter((m) => forMe(m, me.id));
  const unread = tab === "messages" ? 0 : msgs.filter((m) => m.at > seen && m.by !== me.id).length;
  const lastAlert = msgs.find((m) => m.kind === "alert" && m.alert !== "all_clear" && m.at > Date.now() - 15 * 60_000);
  useEffect(() => { if (tab === "messages") setSeen(Date.now()); }, [tab, incident.messages.length]);
  const send = () => {
    const t = text.trim();
    if (!t) return;
    void run(async () => { await emergencyController()?.record("emergency:message", null, { incident: incident.id, text: t.slice(0, 500), to: to || null }); setText(""); });
  };
  const sendAlert = (alert: string) => {
    setAlerting(null);
    void run(() => emergencyController()?.record("emergency:alert", null, { incident: incident.id, alert, place: alertPlace.trim().slice(0, 120) || null }) ?? Promise.resolve(), `${ALERT_TITLE[alert]} sent`);
  };
  const reported = allRooms.filter((r) => incident.reports[r.id]).length;
  const marked = kids.length - counts.unmarked;

  return (
    <div className="pme-screen pme-screen--open">
      <header className="pme-alert" data-drill={incident.drill}>
        <button type="button" className="pme-top__close" onClick={leave} aria-label="Close"><Icon name="close" /></button>
        <div className="pme-alert__text">
          <h1><Icon name={KIND_ICON[incident.kind] ?? "warning"} />{title(incident)}</h1>
          <p>{incident.place ? `${incident.place} · ` : ""}since {clock(incident.declaredAt)}</p>
        </div>
        <button type="button" className="pme-alert__end" onClick={() => setEnding(true)}>End</button>
      </header>
      <p className="pme-link" data-mode={mesh?.mode ?? "online"}>
        <Icon name={mesh ? "link" : "cloud"} />
        {mesh ? `Bluetooth: ${mesh.links} ${mesh.links === 1 ? "device" : "devices"} nearby` : "Connected by internet or Wi-Fi"}
      </p>

      <dl className="pme-stats">
        <div data-tone={counts.missing ? "danger" : undefined}><dt>Missing</dt><dd>{counts.missing}</dd></div>
        <div data-tone={counts.injured ? "warning" : undefined}><dt>Injured</dt><dd>{counts.injured}</dd></div>
        <div data-tone="success"><dt>Safe</dt><dd>{counts.present + counts.with_adult + counts.released}</dd></div>
        <div><dt>To mark</dt><dd>{counts.unmarked}</dd></div>
      </dl>

      <div className="pme-tabs" role="tablist" aria-label="Roll call">
        <button type="button" role="tab" aria-selected={tab === "class"} onClick={() => setTab("class")}>My class <span>{marked}/{kids.length}</span></button>
        <button type="button" role="tab" aria-selected={tab === "school"} onClick={() => setTab("school")}>School <span>{reported}/{allRooms.length}</span></button>
        <button type="button" role="tab" aria-selected={tab === "messages"} onClick={() => setTab("messages")}>Messages{unread > 0 && <span className="pme-unread" aria-label={`${unread} new`}>{unread}</span>}</button>
      </div>
      {lastAlert && tab !== "messages" && (
        <button type="button" className="pme-lastalert" data-alert={lastAlert.alert} onClick={() => setTab("messages")}>
          <b>{ALERT_TITLE[lastAlert.alert ?? ""]}{lastAlert.place ? ` · ${lastAlert.place}` : ""}</b> {staffName(lastAlert.by)}, {clock(lastAlert.at)}
        </button>
      )}

      <div className="pme-scroll" role="tabpanel">
        {tab === "messages" ? (
          msgs.length ? (
            <ul className="pme-msgs">
              {msgs.map((m) => (
                <li key={m.id} className="pme-msg" data-kind={m.kind} data-alert={m.alert ?? undefined} data-mine={m.by === me.id}>
                  {m.kind === "alert"
                    ? <b>{ALERT_TITLE[m.alert ?? ""] ?? "Alert"}{m.place ? ` · ${m.place}` : ""}</b>
                    : <p>{m.text}</p>}
                  <small>{staffName(m.by)}{m.to ? ` → ${m.to === me.id ? "you" : staffName(m.to)}` : ""} · {clock(m.at)}</small>
                </li>
              ))}
            </ul>
          ) : <p className="pme-empty">No messages yet. They reach every staff phone, by internet, Wi-Fi or Bluetooth.</p>
        ) : tab === "class" ? (
          <>
            {incident.kind === "reunification" && (
              <button type="button" className="pme-note" onClick={() => navigate(`${T}/class`)}>
                <b>Release at {incident.place ?? "the meeting point"}</b> Open Class, tap the child, then Release. {counts.released} gone home.
              </button>
            )}
            {board.loading && !board.data && <div className="pmu-center"><Spinner label="Loading your class" /></div>}
            <ul className="pme-rows">
              {kids.map((c) => {
                const s = statusOf(c);
                return (
                  <li key={c.id}>
                    <button type="button" className="pme-row" onClick={() => s !== "released" && setSel(c)} aria-label={`${c.first} ${c.last}: ${s ? STATUS_LABEL[s] : "not marked"}`}>
                      <span className="pme-row__name">{c.first} {c.last}<small>{roomName(c.room_id)}</small></span>
                      <span className="pme-chip" data-status={s ?? "none"}>{s ? STATUS_LABEL[s] : "Mark"}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <>
            {missingIds.length > 0 && (
              <section className="pme-missing" aria-label="Missing children">
                <b>Missing ({missingIds.length})</b>
                <ul>{missingIds.map((id) => <li key={id}>{nameOf(id)}</li>)}</ul>
              </section>
            )}
            <ul className="pme-rows">
              {allRooms.map((r) => {
                const rep = incident.reports[r.id];
                return (
                  <li key={r.id} className="pme-row pme-row--static">
                    <span className="pme-row__name">{r.name}{rep && <small>{rep.accounted} accounted for · {clock(rep.at)}</small>}</span>
                    <span className="pme-chip" data-status={rep ? (rep.complete ? "present" : "missing") : "none"}>{rep ? (rep.complete ? "All safe" : `${rep.missing.length} missing`) : "Not reported"}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {tab === "messages" ? (
        <div className="pme-compose">
          <div className="pme-alertbtns">
            {ALERTS.map((a) => (
              <button key={a.id} type="button" data-alert={a.id} disabled={busy} onClick={() => setAlerting(a.id)}><Icon name={a.icon} />{a.label}</button>
            ))}
          </div>
          <form className="pme-send" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <select aria-label="To" value={to} onChange={(e) => setTo(e.currentTarget.value)}>
              <option value="">Everyone</option>
              {(roster.data?.staff ?? []).filter((st) => st.id !== me.id).map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
            <input aria-label="Message" value={text} maxLength={500} placeholder="Message" onChange={(e) => setText(e.currentTarget.value)} />
            <button type="submit" aria-label="Send" disabled={!text.trim() || busy}><Icon name="send" /></button>
          </form>
        </div>
      ) : (
      <div className="pme-actions">
        <Button variant="secondary" disabled={!counts.unmarked || busy} onClick={() => void markRest()} aria-describedby="pme-rest">Mark everyone else</Button>
        <Button disabled={!kids.length || busy} onClick={() => void report()}>Report my class</Button>
        <p id="pme-rest" className="pme-actions__hint">Mark everyone else: in school as present, the others as absent today.</p>
      </div>
      )}
      <Dialog open={!!alerting} onOpenChange={(o) => { if (!o) setAlerting(null); }} title={alerting ? ALERT_TITLE[alerting]! : ""} description="Every staff phone is alerted at once."
        actions={<><Button variant="secondary" onClick={() => setAlerting(null)}>Cancel</Button><Button variant={alerting === "all_clear" ? "primary" : "danger"} onClick={() => alerting && sendAlert(alerting)}>Send alert</Button></>}>
        <TextField label="Where are you?" value={alertPlace} maxLength={120} onChange={(e) => setAlertPlace(e.currentTarget.value)} placeholder="e.g. Room 12, the gym" />
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => { if (!o) setSel(null); }} title={sel ? `${sel.first} ${sel.last}` : ""} description="Where is this child now?">
        <div className="pme-statuses">
          {STATUS.map((st) => (
            <button key={st.id} type="button" className="pme-status" data-status={st.id} disabled={busy} aria-pressed={!!sel && statusOf(sel) === st.id} onClick={() => sel && mark(sel, st.id)}>{st.label}</button>
          ))}
        </div>
      </Dialog>
      <Dialog open={ending} onOpenChange={setEnding} title={`End the ${incident.drill ? "drill" : "emergency"}?`} description="Every phone and door goes back to normal."
        actions={<><Button variant="secondary" onClick={() => setEnding(false)}>Not yet</Button><Button loading={busy} onClick={() => { void run(async () => { await rec(null, "emergency:end", { note: null }); setEnding(false); }); }}>End it</Button></>} />
    </div>
  );
}

const DOOR_WHAT: Record<string, string> = {
  lockdown: "Lockdown. Stay away from the doors and windows and follow staff instructions.",
  evacuation: "Evacuation. Leave the building calmly and go to the assembly point.",
  shelter: "Shelter in place. Stay inside, away from windows, until staff say it is over.",
};

/**
 * The door during an incident: the whole screen, nothing else to tap. A reunification keeps the
 * door working (adults are released there) under a band instead.
 */
export function DoorEmergency({ children }: { children: ReactNode }) {
  const { incident } = useEmergency();
  if (!isOpen(incident)) return <>{children}</>;
  const kind = KIND_LABEL[incident.kind] ?? "Emergency";
  if (incident.kind === "reunification")
    return (
      <>
        <div className="pme-band" data-drill={incident.drill} role="status">
          <span className="pme-band__kind">{incident.drill ? "Drill · " : ""}{kind}</span>
          <span className="pme-band__where">{incident.place ? `${incident.place} · ` : ""}since {clock(incident.declaredAt)}</span>
        </div>
        {children}
      </>
    );
  return (
    <div className="pme-door" data-drill={incident.drill} role="alert">
      {incident.drill && <p className="pme-door__drill">DRILL</p>}
      <p className="pme-door__kind">{kind}</p>
      <p className="pme-door__what">{DOOR_WHAT[incident.kind] ?? "Follow staff instructions."}{incident.place ? ` ${incident.place}.` : ""}</p>
      <p className="pme-door__when">Since {clock(incident.declaredAt)}</p>
    </div>
  );
}
